import { Tooltip } from '@heroui/react';
import React, { useState } from 'react';
import { FiCheck, FiCopy } from 'react-icons/fi';

import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';

interface CopyButtonProps {
  /** How the value is named to a person, e.g. "login email". */
  name: string;
  value?: string | null;
  testId?: string;
}

/**
 * A small icon that copies one value, for sitting next to a field label.
 * Renders nothing when there is no value to copy.
 */
export const CopyButton: React.FC<CopyButtonProps> = ({
  name,
  value,
  testId,
}) => {
  const { copy, justCopied } = useCopyToClipboard(name);
  // Tooltips close on press. Reopen it so "Copied" is visible after a click.
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  if (!value) return null;

  const handleClick = () => {
    setIsTooltipOpen(true);
    copy(value);
  };

  return (
    <Tooltip
      isOpen={isTooltipOpen}
      onOpenChange={setIsTooltipOpen}
      content={justCopied ? 'Copied' : `Copy ${name}`}
      placement="top"
    >
      <button
        type="button"
        onClick={handleClick}
        data-testid={testId}
        aria-label={`Copy ${name}`}
        className="inline-flex items-center rounded p-1 text-default-500 transition-colors hover:bg-default-100 hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
      >
        {justCopied ? (
          <FiCheck size={14} className="text-success" />
        ) : (
          <FiCopy size={14} />
        )}
      </button>
    </Tooltip>
  );
};
